// metadata/register.ts
import { BaseError } from "@adornis/validation/errors/BaseError.js";
import { MultipleValidationError } from "@adornis/validation/errors/MultipleValidationError.js";
import { ValidationError } from "@adornis/validation/errors/ValidationError.js";
import { GraphQLBoolean, GraphQLFloat, GraphQLID, GraphQLInt, GraphQLString, Kind } from "graphql";
import { DateTime } from "luxon";
import { Float, ID, Int, isDateTime } from "../baseqlTypes.js";
import { NotAuthorizedError } from "../errors/NotAuthorizedError.js";
import { NotSupportedError } from "../errors/NotSupportedError.js";
import { PermissionsNotSetError } from "../errors/PermissionsNotSetError.js";
import { fieldParentSymbol } from "../symbols.js";
import { baseqlMetaData, getOperationDict, metaState } from "./metaDataStore.js";
import { registerOperation } from "./operations.js";
import "./types.js";
import { BaseQLUnion } from "./union.js";
function registerError(errorClass) {
  if (baseqlMetaData.errors.find((op) => op.name === errorClass._class))
    throw new Error(`Trying to register duplicate error ${errorClass._class}.`);
  baseqlMetaData.errors.push(errorClass);
}
var registerScalar = (scalar) => {
  if (!scalar.scalar && !scalar.customScalarConfig)
    throw new Error("Cannot register a BaseQLScalar with neither options.scalar nor options.customScalarConfig");
  if (baseqlMetaData.scalars[scalar.name])
    throw new Error(
      `Trying to register scalar with name "${scalar.name}", but a scalar with this name already exists.`
    );
  const type = scalar.type;
  baseqlMetaData.scalars[scalar.name] = { isTypeOf: (t) => t === type, ...scalar };
};
var registerUnion = (union) => {
  if (baseqlMetaData.unions[union.name])
    throw new Error(`Trying to register union with name "${union.name}", but a union with this name already exists.`);
  const unionInstance = new BaseQLUnion(union.name);
  baseqlMetaData.unions[union.name] = { ...union, type: unionInstance };
  return unionInstance;
};
var registerEnum = (enumDef) => {
  if (baseqlMetaData.enums[enumDef.name])
    throw new Error(`Trying to register enum with name "${enumDef.name}", but a scalar with this name already exists.`);
  enumDef.enum._class = enumDef.name;
  enumDef.enum._isBaseQLEnum = true;
  baseqlMetaData.enums[enumDef.name] = enumDef;
};
var registerEntity = (entity, options = {}, dynamic = false) => {
  if (metaState.sealed && !dynamic)
    throw new Error(
      `Trying to register Entity with _class "${entity._class}" after generateSchema was already called. It will not be included in the schema.
Check import order and make sure all Entities are registerd before generateSchema is called`
    );
  if (!entity._class)
    throw new Error(`Entity ${entity} has no or empty _class property.`);
  let fields = entity.prototype.fields;
  if (!fields) {
    console.warn(`Entity ${entity._class} has no field metadata.`);
    fields = {};
  }
  if (fields[fieldParentSymbol] !== entity.prototype) {
    const parentFields = Object.fromEntries(
      Object.entries(fields).map(([key, value]) => [key, { ...value, fromParent: true }])
    );
    fields = { ...parentFields, [fieldParentSymbol]: entity.prototype };
    entity.prototype.fields = fields;
  }
  const proto = Object.getPrototypeOf(entity);
  const protoNode = baseqlMetaData.entities.find((node) => node.type() === proto);
  if (!protoNode)
    throw new Error(
      `Superclass ${proto._class} of ${entity._class} is not (yet?) present in dependency tree. Check your initialization order!`
    );
  protoNode.children.push({
    type: () => entity,
    _class: entity._class,
    parent: protoNode,
    fields,
    options,
    dynamic,
    children: []
  });
};
var registerQuery = (options) => {
  return registerOperation({ ...options, opType: "query", entity: null });
};
var registerMutation = (options) => {
  return registerOperation({ ...options, opType: "mutation", entity: null });
};
var registerSubscription = (options) => {
  return registerOperation({ ...options, opType: "subscription", entity: null });
};
var registerMiddleware = ({
  middleware,
  opType,
  operationName,
  splice
}) => {
  const operations = getOperationDict(opType);
  const operation = operations[operationName];
  if (!operation)
    throw new Error(
      `Trying to register a middleware on operation, but ${opType} ${operationName} could not be found in baseqlMetaData`
    );
  const { index = operation.middlewares.length, deleteCount = 0 } = splice ?? {};
  operation.middlewares.splice(index, deleteCount, middleware);
};
var registerInternals = () => {
  registerError(BaseError);
  registerError(ValidationError);
  registerError(MultipleValidationError);
  registerError(NotAuthorizedError);
  registerError(PermissionsNotSetError);
  registerError(NotSupportedError);
  registerScalar({
    name: GraphQLID.name,
    scalar: GraphQLID,
    coerceValue: (value) => String(value),
    type: ID
  });
  registerScalar({
    name: GraphQLString.name,
    scalar: GraphQLString,
    coerceValue: (value) => String(value),
    type: String
  });
  registerScalar({
    name: GraphQLBoolean.name,
    scalar: GraphQLBoolean,
    coerceValue: (value) => Boolean(value),
    type: Boolean
  });
  registerScalar({
    name: GraphQLInt.name,
    scalar: GraphQLInt,
    coerceValue: (value) => Number(value),
    type: Int
  });
  registerScalar({
    name: GraphQLFloat.name,
    scalar: GraphQLFloat,
    coerceValue: (value) => Number(value),
    type: Float
  });
  registerScalar({
    name: "Date",
    customScalarConfig: {
      description: "Date custom scalar type",
      parseValue(value) {
        if (typeof value !== "string")
          throw new Error("Date scalar cannot parse non string values");
        return new Date(value);
      },
      serialize(value) {
        return DateTime.isDateTime(value) ? value.toISO() : value;
      },
      parseLiteral(ast) {
        if (ast.kind === Kind.STRING) {
          return DateTime.fromISO(ast.value);
        }
        return null;
      }
    },
    coerceValue(value) {
      return new Date(value);
    },
    type: Date
  });
  registerScalar({
    name: "DateTime",
    customScalarConfig: {
      description: "Luxon DateTime custom scalar type",
      parseValue(value) {
        if (typeof value !== "string")
          throw new Error("DateTime scalar cannot parse non string values");
        return DateTime.fromISO(value);
      },
      serialize(value) {
        return DateTime.isDateTime(value) ? value.toISO() : value;
      },
      parseLiteral(ast) {
        if (ast.kind === Kind.STRING) {
          return DateTime.fromISO(ast.value);
        }
        return null;
      }
    },
    coerceValue(value) {
      if (DateTime.isDateTime(value))
        return value;
      if (typeof value === "string")
        return DateTime.fromISO(value);
      if (typeof value === "number")
        return DateTime.fromMillis(value);
      return DateTime.fromJSDate(value);
    },
    type: DateTime,
    isTypeOf(type) {
      return isDateTime(type);
    },
    toJSPrimitive(value) {
      return DateTime.isDateTime(value) ? value.toJSDate() : value;
    }
  });
};
registerInternals();
export {
  registerEntity,
  registerEnum,
  registerError,
  registerInternals,
  registerMiddleware,
  registerMutation,
  registerQuery,
  registerScalar,
  registerSubscription,
  registerUnion
};
